import React, { useEffect, useRef, useState } from 'react';
import { GlassPage } from '../components/chrome';

// Internal test page for the self-hosted model on korsa-server (replacing
// Groq). Not linked anywhere and marked noindex. nginx proxies /api/llm/ to the
// PC's gateway and adds the API key, so no key lives in this page.
const ENDPOINT = '/api/llm/v1/chat/completions';
const SYSTEM = 'You are Korsa, the assistant in Korsa smart glasses. Answer clearly and briefly.';

// Reads an OpenAI-style server-sent-event stream, calling onText per token.
async function streamChat(messages, onText, signal) {
  const res = await fetch(ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model: 'korsa',
      stream: true,
      reasoning_effort: 'low',
      temperature: 0.3,
      max_tokens: 1500,
      messages: [{ role: 'system', content: SYSTEM }, ...messages],
    }),
    signal,
  });
  if (res.status === 429) throw new Error('Too many messages. Wait a minute and try again.');
  if (!res.ok || !res.body) throw new Error(`The server didn't answer (${res.status}). The PC may be off.`);
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop();
    for (const line of lines) {
      const data = line.replace(/^data: ?/, '').trim();
      if (!data || data === '[DONE]' || !line.startsWith('data:')) continue;
      try {
        const text = JSON.parse(data).choices?.[0]?.delta?.content;
        if (text) onText(text);
      } catch (e) {
        /* partial line, ignore */
      }
    }
  }
}

const ChatbotTest = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [stats, setStats] = useState('');
  const bottomRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    const history = [...messages, { role: 'user', content: text }];
    setMessages([...history, { role: 'assistant', content: '' }]);
    setInput('');
    setError('');
    setBusy(true);
    const controller = new AbortController();
    abortRef.current = controller;
    const started = performance.now();
    let first = 0;
    let chunks = 0;
    try {
      await streamChat(
        history,
        (t) => {
          if (!first) first = performance.now();
          chunks += 1;
          setMessages((m) => {
            const copy = m.slice();
            copy[copy.length - 1] = { role: 'assistant', content: copy[copy.length - 1].content + t };
            return copy;
          });
        },
        controller.signal
      );
      if (first) {
        const secs = (performance.now() - first) / 1000;
        setStats(
          `First word in ${((first - started) / 1000).toFixed(1)}s` +
            (secs > 0.2 ? ` · ~${Math.round(chunks / secs)} tokens/s` : '')
        );
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(err.message);
        setMessages(history);
      }
    } finally {
      setBusy(false);
      abortRef.current = null;
    }
  };

  return (
    <GlassPage>
      <div className="max-w-2xl mx-auto px-4 md:px-6 pt-24 md:pt-32 pb-16">
        <section className="glass px-5 sm:px-8 py-8 md:py-10">
          <h1 className="text-2xl md:text-3xl font-bold tracking-tighter uppercase text-zinc-900 mb-1">Chatbot test</h1>
          <p className="text-xs text-zinc-600 mb-6">
            Korsa's own AI model, running on our server. Internal test page.
          </p>

          <div className="space-y-3 mb-6 min-h-[200px] max-h-[55vh] overflow-y-auto" aria-live="polite" data-lenis-prevent>
            {messages.length === 0 && (
              <p className="text-sm text-zinc-500">Ask anything to start.</p>
            )}
            {messages.map((m, i) => (
              <div key={i} className={m.role === 'user' ? 'text-right' : 'text-left'}>
                <div
                  className={`inline-block max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap text-left ${
                    m.role === 'user' ? 'bg-zinc-900 text-white' : 'bg-white/70 text-zinc-900'
                  }`}
                >
                  <span className="sr-only">{m.role === 'user' ? 'You: ' : 'Korsa: '}</span>
                  {m.content || (busy && i === messages.length - 1 ? 'Thinking…' : '')}
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>

          {error && (
            <p role="alert" className="text-sm font-bold text-red-700 mb-3">{error}</p>
          )}

          <form onSubmit={send} className="flex gap-2">
            <label htmlFor="chat-input" className="sr-only">Message</label>
            <input
              id="chat-input"
              type="text"
              autoComplete="off"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type a message"
              className="glass-input flex-1 min-w-0 px-4 py-3"
            />
            {busy ? (
              <button type="button" onClick={() => abortRef.current?.abort()} className="btn-dark px-5">Stop</button>
            ) : (
              <button type="submit" disabled={!input.trim()} className="btn-dark px-5">Send</button>
            )}
          </form>
          <div className="flex justify-between mt-3 text-[11px] text-zinc-500">
            <span>{stats}</span>
            {messages.length > 0 && !busy && (
              <button type="button" onClick={() => { setMessages([]); setStats(''); }} className="underline">
                New chat
              </button>
            )}
          </div>
        </section>
      </div>
    </GlassPage>
  );
};

export default ChatbotTest;
